import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  MODULO_COBROS,
  authConfig,
  fechaHoyInput,
  formatearMoneda,
  manejarErrorSesion,
  nombreInstructor,
  obtenerId,
  obtenerModuloPorNombre,
  tienePermiso,
  toInputDate,
} from "../utils/cobros";

const RUTA_LISTADO = "/cobros/suscripciones";

const sumarDias = (fechaTexto, dias) => {
  if (!fechaTexto) return "";
  const fecha = new Date(`${fechaTexto}T12:00:00`);
  fecha.setDate(fecha.getDate() + Number(dias || 0));
  return fecha.toISOString().split("T")[0];
};

const sumarMeses = (fechaTexto, meses) => {
  if (!fechaTexto) return "";
  const fecha = new Date(`${fechaTexto}T12:00:00`);
  const diaOriginal = fecha.getDate();
  fecha.setDate(1);
  fecha.setMonth(fecha.getMonth() + Number(meses || 0));
  const ultimoDia = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0).getDate();
  fecha.setDate(Math.min(diaOriginal, ultimoDia));
  return fecha.toISOString().split("T")[0];
};

const calcularFin = (inicio, plan) => {
  if (!inicio || !plan) return "";
  const cantidad = Number(plan.cantidadDuracion || 0);
  const tipo = String(plan.tipoDuracion || "MESES").toUpperCase();
  if (cantidad <= 0) return "";
  if (tipo === "DIAS") return sumarDias(inicio, cantidad);
  if (tipo === "ANIOS") return sumarMeses(inicio, cantidad * 12);
  if (tipo === "MESES") return sumarMeses(inicio, cantidad);
  return "";
};

const RegistrarSuscripciones = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = Boolean(id);

  const [instructores, setInstructores] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [planObjectId, setPlanObjectId] = useState("");
  const [tipoRegistro, setTipoRegistro] = useState("COMERCIAL");
  const [origenSuscripcion, setOrigenSuscripcion] = useState("PAGO");
  const [fechaInicio, setFechaInicio] = useState(fechaHoyInput());
  const [estadoSuscripcion, setEstadoSuscripcion] = useState("");
  const [pagoObjectId, setPagoObjectId] = useState("");
  const [renovacionAutomatica, setRenovacionAutomatica] = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [loading, setLoading] = useState(false);
  const [cargandoBase, setCargandoBase] = useState(false);

  const modulo = useMemo(() => obtenerModuloPorNombre(MODULO_COBROS), []);
  const puedeCrear = useMemo(() => tienePermiso(modulo, "CREAR"), [modulo]);
  const puedeLeer = useMemo(() => tienePermiso(modulo, "LEER"), [modulo]);
  const puedeActualizar = useMemo(() => tienePermiso(modulo, "ACTUALIZAR"), [modulo]);
  const tienePermisoFormulario = edit ? puedeActualizar : puedeCrear;

  const planSeleccionado = useMemo(
    () => planes.find((plan) => String(plan._id) === String(planObjectId)) || null,
    [planes, planObjectId]
  );

  const fechaFinCalculada = useMemo(
    () => (tipoRegistro === "ADMINISTRATIVA" ? calcularFin(fechaInicio, planSeleccionado) : ""),
    [fechaInicio, planSeleccionado, tipoRegistro]
  );

  const fechaFinGraciaCalculada = useMemo(() => {
    if (!fechaFinCalculada) return "";
    const diasGracia = Number(planSeleccionado?.diasGracia || 0);
    return diasGracia > 0 ? sumarDias(fechaFinCalculada, diasGracia) : "";
  }, [fechaFinCalculada, planSeleccionado]);

  const bloqueadaPorPago = edit && (Boolean(pagoObjectId) || origenSuscripcion === "RENOVACION");

  useEffect(() => {
    const cargarBase = async () => {
      try {
        setCargandoBase(true);
        const [resInstructores, resPlanes] = await Promise.all([
          axios.get("/api/instructores/listall", authConfig()),
          axios.get("/api/planes/listall", authConfig()),
        ]);
        setInstructores(Array.isArray(resInstructores.data?.instructores) ? resInstructores.data.instructores : []);
        setPlanes(Array.isArray(resPlanes.data?.planes) ? resPlanes.data.planes : []);
      } catch (error) {
        console.error("Error cargando datos base:", error);
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", "No se pudieron cargar instructores y planes.", "error");
      } finally {
        setCargandoBase(false);
      }
    };
    cargarBase();
  }, [navigate]);

  useEffect(() => {
    if (!id) return;

    const cargar = async () => {
      if (!puedeLeer && !puedeActualizar) return;
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/suscripciones/listid/${id}`, authConfig());
        const suscripcion = data?.suscripcion || data?.message;
        if (!suscripcion) throw new Error("No se pudo cargar la suscripción.");

        const origen = String(suscripcion.origenSuscripcion || "PAGO").toUpperCase();
        setInstructorObjectId(obtenerId(suscripcion.instructorObjectId));
        setPlanObjectId(obtenerId(suscripcion.planObjectId));
        setOrigenSuscripcion(origen);
        setTipoRegistro(["PAGO", "RENOVACION"].includes(origen) ? "COMERCIAL" : "ADMINISTRATIVA");
        setFechaInicio(toInputDate(suscripcion.fechaInicio) || fechaHoyInput());
        setEstadoSuscripcion(suscripcion.estadoSuscripcion || "");
        setPagoObjectId(obtenerId(suscripcion.pagoObjectId));
        setRenovacionAutomatica(Boolean(suscripcion.renovacionAutomatica));
        setObservaciones(suscripcion.observaciones || "");
      } catch (error) {
        console.error("Error cargando suscripción:", error);
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || error.message, "error");
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [id, navigate, puedeActualizar, puedeLeer]);

  const cambiarTipo = (tipo) => {
    setTipoRegistro(tipo);
    if (tipo === "COMERCIAL") {
      setOrigenSuscripcion("PAGO");
    } else if (origenSuscripcion === "PAGO") {
      setOrigenSuscripcion("ADMINISTRADOR");
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermisoFormulario) {
      Swal.fire("Sin permisos", "No tiene permisos para guardar suscripciones.", "warning");
      return;
    }

    if (!instructorObjectId || !planObjectId) {
      Swal.fire("Atención", "Instructor y plan son obligatorios.", "warning");
      return;
    }

    if (!planSeleccionado || planSeleccionado.estadoPlan === false) {
      Swal.fire("Atención", "Seleccione un plan activo y válido.", "warning");
      return;
    }

    if (tipoRegistro === "ADMINISTRATIVA" && !fechaInicio) {
      Swal.fire("Atención", "La fecha de inicio es obligatoria para una asignación administrativa.", "warning");
      return;
    }

    const payload = {
      instructorObjectId,
      planObjectId,
      origenSuscripcion: tipoRegistro === "COMERCIAL" ? "PAGO" : origenSuscripcion,
      renovacionAutomatica,
      observaciones: observaciones.trim(),
    };

    if (tipoRegistro === "ADMINISTRATIVA") payload.fechaInicio = fechaInicio;

    try {
      setLoading(true);
      const { data } = edit
        ? await axios.put(`/api/suscripciones/update/${id}`, payload, authConfig())
        : await axios.post("/api/suscripciones/add", payload, authConfig());

      const suscripcion = data?.suscripcion;

      if (!edit && suscripcion?.estadoSuscripcion === "PENDIENTE_PAGO") {
        const result = await Swal.fire({
          icon: "success",
          title: "Suscripción registrada",
          text: "La suscripción está PENDIENTE DE PAGO. ¿Desea registrar el pago ahora?",
          showCancelButton: true,
          confirmButtonText: "Registrar pago",
          cancelButtonText: "Volver a suscripciones",
        });

        if (result.isConfirmed) {
          navigate(`/cobros/pagos/nuevo?concepto=SUSCRIPCION&suscripcionId=${suscripcion._id}`);
        } else {
          navigate(RUTA_LISTADO);
        }
        return;
      }

      await Swal.fire({
        icon: "success",
        title: data?.message || (edit ? "Suscripción actualizada" : "Asignación creada"),
        showConfirmButton: false,
        timer: 1700,
      });
      navigate(RUTA_LISTADO);
    } catch (error) {
      console.error("Error guardando suscripción:", error);
      if (manejarErrorSesion(error, navigate)) return;
      Swal.fire({
        icon: "error",
        title: edit ? "Error al actualizar" : "Error al registrar",
        text: error.response?.data?.message || error.message || "No se pudo guardar la suscripción.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (edit && !puedeActualizar) {
    return <div className="container py-5"><div className="alert alert-warning">No tiene permisos para actualizar suscripciones.</div></div>;
  }

  if (!edit && !puedeCrear) {
    return <div className="container py-5"><div className="alert alert-warning">No tiene permisos para crear suscripciones.</div></div>;
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h4 className="mb-1 fw-semibold">{edit ? "Actualizar Suscripción" : "Registrar Suscripción"}</h4>
            <p className="mb-0 text-secondary small">
              Una suscripción comercial queda PENDIENTE DE PAGO y solo inicia vigencia cuando el pago se aprueba.
            </p>
          </div>
          <button type="button" className="btn btn-outline-dark" onClick={() => navigate(RUTA_LISTADO)} disabled={loading}>
            <i className="fa-solid fa-arrow-left me-2"></i>Volver
          </button>
        </div>
      </div>

      <form onSubmit={actions}>
        <div className="row g-3">
          <div className="col-12 col-xl-8">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
              {!edit && (
                <>
                  <h6 className="fw-semibold">Tipo de registro</h6>
                  <div className="row g-2 mb-4">
                    <div className="col-12 col-md-6">
                      <button
                        type="button"
                        className={`btn w-100 ${tipoRegistro === "COMERCIAL" ? "btn-dark" : "btn-outline-dark"}`}
                        onClick={() => cambiarTipo("COMERCIAL")}
                      >
                        <i className="fa-solid fa-credit-card me-2"></i>Suscripción comercial
                      </button>
                    </div>
                    <div className="col-12 col-md-6">
                      <button
                        type="button"
                        className={`btn w-100 ${tipoRegistro === "ADMINISTRATIVA" ? "btn-dark" : "btn-outline-dark"}`}
                        onClick={() => cambiarTipo("ADMINISTRATIVA")}
                      >
                        <i className="fa-solid fa-gift me-2"></i>Asignación sin pago
                      </button>
                    </div>
                  </div>
                </>
              )}

              <h6 className="fw-semibold">Asignación</h6>
              <hr />
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small text-secondary text-uppercase">Instructor *</label>
                  <select
                    className="form-select"
                    value={instructorObjectId}
                    onChange={(e) => setInstructorObjectId(e.target.value)}
                    disabled={cargandoBase || loading || bloqueadaPorPago}
                    required
                  >
                    <option value="">{cargandoBase ? "Cargando..." : "Seleccione"}</option>
                    {instructores.map((instructor) => (
                      <option key={instructor._id} value={instructor._id}>
                        {nombreInstructor(instructor)} - {instructor.documentoInstructor || "Sin documento"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label small text-secondary text-uppercase">Plan *</label>
                  <select
                    className="form-select"
                    value={planObjectId}
                    onChange={(e) => setPlanObjectId(e.target.value)}
                    disabled={cargandoBase || loading || bloqueadaPorPago}
                    required
                  >
                    <option value="">Seleccione</option>
                    {planes.map((plan) => (
                      <option key={plan._id} value={plan._id} disabled={plan.estadoPlan === false}>
                        {plan.codigoPlan} - {plan.nombrePlan} - {formatearMoneda(plan.valorPlan, plan.moneda)}
                      </option>
                    ))}
                  </select>
                </div>

                {tipoRegistro === "ADMINISTRATIVA" && (
                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">Fecha inicio *</label>
                    <input type="date" className="form-control" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} disabled={loading || bloqueadaPorPago} required />
                  </div>
                )}

                {tipoRegistro === "ADMINISTRATIVA" && (
                  <>
                    <div className="col-12 col-md-4">
                      <label className="form-label small text-secondary text-uppercase">Fin calculado</label>
                      <input type="date" className="form-control bg-light" value={fechaFinCalculada} readOnly />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small text-secondary text-uppercase">Fin gracia</label>
                      <input type="date" className="form-control bg-light" value={fechaFinGraciaCalculada} readOnly />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
              <h6 className="fw-semibold">Observaciones</h6>
              <hr />
              <textarea className="form-control" rows="4" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Observaciones administrativas..." disabled={loading} />
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
              <h6 className="fw-semibold">Estado y origen</h6>
              <hr />

              {edit && (
                <div className="mb-3">
                  <label className="form-label small text-secondary text-uppercase">Estado actual</label>
                  <input className="form-control bg-light" value={String(estadoSuscripcion || "").replaceAll("_", " ")} readOnly />
                </div>
              )}

              {tipoRegistro === "COMERCIAL" ? (
                <div className="alert alert-warning">
                  <div className="fw-semibold mb-1">Suscripción comercial</div>
                  {!edit || estadoSuscripcion === "PENDIENTE_PAGO"
                    ? "Se guardará PENDIENTE DE PAGO. La fecha de inicio se asignará cuando el pago sea aprobado."
                    : "Este período fue generado o activado por pago. Plan, origen y fechas quedan protegidos."}
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label small text-secondary text-uppercase">Origen *</label>
                  <select className="form-select" value={origenSuscripcion} onChange={(e) => setOrigenSuscripcion(e.target.value)} disabled={loading || bloqueadaPorPago}>
                    <option value="ADMINISTRADOR">Administrador</option>
                    <option value="PRUEBA">Prueba</option>
                    <option value="CORTESIA">Cortesía</option>
                  </select>
                </div>
              )}

              <div className="form-check form-switch mt-3">
                <input className="form-check-input" type="checkbox" id="renovacionAutomatica" checked={renovacionAutomatica} onChange={(e) => setRenovacionAutomatica(e.target.checked)} disabled={loading} />
                <label className="form-check-label" htmlFor="renovacionAutomatica">Renovación automática</label>
              </div>

              {planSeleccionado && (
                <div className="alert alert-light border mt-4 mb-0 small">
                  <div className="fw-semibold mb-2">{planSeleccionado.nombrePlan}</div>
                  <div className="text-secondary">Valor: {formatearMoneda(planSeleccionado.valorPlan, planSeleccionado.moneda)}</div>
                  <div className="text-secondary">Duración: {planSeleccionado.cantidadDuracion} {String(planSeleccionado.tipoDuracion || "").toLowerCase()}</div>
                  <div className="text-secondary">Gracia: {Number(planSeleccionado.diasGracia || 0)} día(s)</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <button type="submit" className="btn btn-dark w-100 fw-semibold py-2" disabled={loading || cargandoBase || !tienePermisoFormulario}>
                <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"} me-2`}></i>
                {loading ? "Guardando…" : edit ? "Guardar cambios" : tipoRegistro === "COMERCIAL" ? "Registrar suscripción" : "Crear asignación"}
              </button>
            </div>
            <div className="col-12 col-md-6">
              <button type="button" className="btn btn-outline-secondary w-100 fw-semibold py-2" onClick={() => navigate(RUTA_LISTADO)} disabled={loading}>Cancelar</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrarSuscripciones;
